package com.islamicmedia.site.pages.isitsafe

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.Page
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

/**
 * Public "Is it safe?" hub.
 *
 * For HUMANS: a full-screen interactive 3D gallery (an inverted-sphere / "skybox" of cover art you
 * look around inside; click a cover for the full analysis). It's a self-contained Three.js page served
 * as a static asset ([/isitsafe-sphere.html]) and embedded here in a full-viewport iframe.
 *
 * For SEARCH ENGINES: a Three.js <canvas> isn't crawlable, so the page also renders a visually-hidden
 * block with the headline, blurb, and links to the per-title review pages (/isitsafe/<slug>) — keeping
 * this page's SEO and the internal links to the indexed detail pages intact.
 *
 * To change the gallery lineup, edit the data baked into isitsafe-sphere.html (regen via the helper
 * script). To change the SEO link list, edit [SEO_TITLES] (only titles with a real /isitsafe/<slug>).
 */
private class SeoTitle(val slug: String, val title: String, val score: Int)

// Only titles that have a real /isitsafe/<slug> detail page (for crawlable internal links).
private val SEO_TITLES = listOf(
    SeoTitle("paw-patrol", "PAW Patrol", 9),
    SeoTitle("wall-e", "WALL-E", 8),
    SeoTitle("monsters-inc", "Monsters, Inc.", 8),
    SeoTitle("bluey", "Bluey", 7),
    SeoTitle("harry-potter", "Harry Potter", 5),
    SeoTitle("lion-king", "The Lion King", 5),
    SeoTitle("moana", "Moana", 4),
    SeoTitle("minecraft", "Minecraft", 3),
    SeoTitle("roblox", "Roblox", 1),
)

private fun verdict(score: Int): String = when {
    score >= 7 -> "Generally safe"
    score >= 4 -> "Watch with care"
    else -> "Not recommended"
}

@Page("/isitsafe")
@Composable
fun IsItSafePage() {
    LaunchedEffect(Unit) {
        document.title = "Is it safe? Movies, shows, games & books for Muslim kids | Fitrah Filter"
        val m = document.querySelector("meta[name=\"description\"]")
            ?: document.createElement("meta").also { it.setAttribute("name", "description"); document.head?.appendChild(it) }
        m.setAttribute(
            "content",
            "Is it safe for Muslim kids? Explore an interactive gallery of movies, shows, games, and books, each checked against Islamic values, with the Qur'an behind every concern."
        )
        // Mount the full-screen interactive gallery (Three.js) for human visitors.
        val root = document.getElementById("isafe-sphere")
        if (root != null && root.childElementCount == 0) {
            val f = document.createElement("iframe") as HTMLElement
            f.setAttribute("src", "/isitsafe-sphere.html")
            f.setAttribute("title", "Is it safe? Interactive gallery")
            f.style.cssText = "position:fixed;inset:0;width:100%;height:100%;border:0;display:block;z-index:1000;"
            root.appendChild(f)
        }
    }

    // Full-screen mount point for the gallery (teal placeholder until the iframe paints).
    Div(attrs = {
        id("isafe-sphere")
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "#0E7C7B"); property("z-index", "1000")
        }
    }) {}

    // Visually-hidden, crawler-accessible content — preserves SEO + internal links to the detail pages.
    Div(attrs = {
        style {
            property("position", "absolute")
            property("width", "1px"); property("height", "1px")
            property("padding", "0"); property("margin", "-1px")
            property("overflow", "hidden"); property("clip", "rect(0, 0, 0, 0)")
            property("white-space", "normal"); property("border", "0")
        }
    }) {
        H1 { Text("Is it safe? Movies, shows, games & books for Muslim kids") }
        P {
            Text(
                "Quick, Qur'an-grounded verdicts on the titles families ask about most, checked against " +
                    "Islamic values. Explore the interactive gallery, or open a title below for the full breakdown."
            )
        }
        Ul {
            SEO_TITLES.forEach { t ->
                Li {
                    A(href = "/isitsafe/${t.slug}") { Text("Is ${t.title} safe for Muslim kids? ${t.score}/10, ${verdict(t.score)}") }
                }
            }
        }
    }
}
